import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, CaretLeft, MapPin, Users, Plus } from "@phosphor-icons/react";

const PHOTOS = [
  "https://images.unsplash.com/photo-1453828423292-392a660a502f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1583147061993-f75642d915a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1528569937393-ee892b976859?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
];

const MEMORIES = [
  { title: "My first day at school", cat: "Education & Growth", date: "March 3, 1994", place: "Cebu", who: "Mum & Dad", note: "New shoes, a red bag, and butterflies in my tummy." },
  { title: "Learning to ride a bike", cat: "Joyful Moments", date: "June 12, 1995", place: "Our street", who: "My brother", note: "He let go of the seat and I flew." },
  { title: "Sunday at grandma's house", cat: "Love & Family", date: "August 1996", place: "Nana's kitchen", who: "The whole family", note: "The smell of warm bread still takes me back." },
];

const THEMES = {
  scrapbook: {
    name: "Warm Scrapbook",
    page: { backgroundColor: "#FDFBF7", color: "#4A4540" },
    serif: '"Playfair Display", serif',
    script: '"Caveat", cursive',
    sans: '"Outfit", sans-serif',
    accent: "#B08D57",
    soft: "#7EA193",
    desc: "Cozy, nostalgic — tilted polaroids, washi tape, handwritten dates.",
  },
  editorial: {
    name: "Elegant Editorial",
    page: { backgroundColor: "#FBFAF7", color: "#1E1B18" },
    serif: '"Cormorant Garamond", serif',
    script: '"Cormorant Garamond", serif',
    sans: '"DM Sans", sans-serif',
    accent: "#B08D57",
    soft: "#8A7F76",
    desc: "Refined magazine feel — gold accents, airy whitespace, large serif.",
  },
  minimal: {
    name: "Calm Minimal",
    page: { backgroundColor: "#F5F7FA", color: "#33383F" },
    serif: '"DM Sans", sans-serif',
    script: '"DM Sans", sans-serif',
    sans: '"DM Sans", sans-serif',
    accent: "#6C849B",
    soft: "#8FA7A0",
    desc: "Soft, friendly, accessible — rounded cards, muted pastels, lots of air.",
  },
  heritage: {
    name: "Heritage Journal",
    page: { backgroundColor: "#F1E7D0", color: "#3A3225" },
    serif: '"Fraunces", serif',
    script: '"Fraunces", serif',
    sans: '"Fraunces", serif',
    accent: "#6E2A2A",
    soft: "#3B5340",
    desc: "Dignified heirloom — parchment, sepia photos, classic serif.",
  },
};

export default function DesignPreview() {
  const navigate = useNavigate();
  const [key, setKey] = useState("scrapbook");
  const t = THEMES[key];

  return (
    <div className="min-h-screen bg-[#2C2A28]">
      {/* Switcher bar */}
      <div className="sticky top-0 z-20 border-b border-white/10 bg-[#2C2A28]/95 px-6 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="preview-back-btn" className="flex items-center gap-1 text-sm text-white/60 hover:text-white">
            <CaretLeft size={16} /> Back
          </button>
          <span className="mr-2 font-sans text-sm text-white/50">Compare styles:</span>
          {Object.entries(THEMES).map(([k, th]) => (
            <button
              key={k}
              data-testid={`theme-btn-${k}`}
              onClick={() => setKey(k)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                key === k ? "bg-white text-[#2C2A28]" : "bg-white/10 text-white/80 hover:bg-white/20"
              }`}
            >
              {th.name}
            </button>
          ))}
        </div>
      </div>

      {/* Preview canvas */}
      <div className="px-4 py-8">
        <div
          data-testid="preview-canvas"
          className="mx-auto max-w-5xl overflow-hidden rounded-xl shadow-2xl"
          style={{ ...t.page, fontFamily: t.sans }}
        >
          <PreviewContent t={t} themeKey={key} />
        </div>
        <p className="mx-auto mt-4 max-w-5xl text-center text-sm text-white/50">
          <strong style={{ color: "#fff" }}>{t.name}</strong> — {t.desc}
        </p>
      </div>
    </div>
  );
}

function PreviewContent({ t, themeKey }) {
  const rounded = themeKey === "minimal";
  const sepia = themeKey === "heritage";
  const tilt = themeKey === "scrapbook";

  return (
    <div className="px-8 py-8 sm:px-12">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-5" style={{ borderColor: `${t.accent}22` }}>
        <div className="flex items-center gap-2">
          <BookOpen size={24} weight="duotone" style={{ color: t.accent }} />
          <span style={{ fontFamily: t.serif, fontSize: "1.35rem" }}>Your Life Journey</span>
        </div>
        <button
          className="flex items-center gap-1 px-4 py-2 text-sm text-white"
          style={{ backgroundColor: t.accent, borderRadius: rounded ? "999px" : themeKey === "editorial" ? "0px" : "6px" }}
        >
          <Plus size={15} weight="bold" /> Add memory
        </button>
      </div>

      {/* Hero line */}
      <div className="mt-8">
        {themeKey === "editorial" && <p className="mb-1 text-xs uppercase tracking-[0.25em]" style={{ color: t.soft }}>A life in moments</p>}
        {(themeKey === "scrapbook") && <p style={{ fontFamily: t.script, fontSize: "1.8rem", color: t.accent, lineHeight: 1 }}>hello, Grace</p>}
        {(themeKey === "heritage") && <p style={{ fontFamily: t.script, fontStyle: "italic", fontSize: "1.4rem", color: t.soft }}>Chapter One</p>}
        {(themeKey === "minimal") && <p className="text-sm font-medium" style={{ color: t.accent }}>Hello, Grace 👋</p>}
        <h1 style={{ fontFamily: t.serif, fontSize: themeKey === "editorial" ? "3.4rem" : "2.6rem", lineHeight: 1.05, marginTop: 4 }}>
          Childhood
        </h1>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-3 gap-3">
        {[["3", "Memories"], ["2", "Feelings tagged"], ["4", "People remembered"]].map(([v, l]) => (
          <div
            key={l}
            className="p-4"
            style={{
              backgroundColor: themeKey === "minimal" ? "#fff" : "transparent",
              border: themeKey === "editorial" ? "none" : `1px solid ${t.accent}22`,
              borderRadius: rounded ? "16px" : themeKey === "editorial" ? "0" : "8px",
              boxShadow: themeKey === "minimal" ? "0 2px 10px rgba(0,0,0,0.04)" : "none",
              borderTop: themeKey === "editorial" ? `2px solid ${t.accent}` : undefined,
            }}
          >
            <div style={{ fontFamily: t.serif, fontSize: "2rem" }}>{v}</div>
            <div className="text-xs uppercase tracking-wide" style={{ color: t.soft }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Memories */}
      <div className={`mt-8 ${themeKey === "editorial" ? "space-y-6" : "flex flex-wrap gap-6"}`}>
        {MEMORIES.map((m, i) => (
          themeKey === "editorial" ? (
            <div key={i} className="flex gap-5 border-b pb-6" style={{ borderColor: `${t.accent}18` }}>
              <img src={PHOTOS[i]} alt="" className="h-28 w-28 flex-shrink-0 object-cover" />
              <div>
                <p className="text-xs uppercase tracking-[0.2em]" style={{ color: t.accent }}>{m.cat}</p>
                <h3 style={{ fontFamily: t.serif, fontSize: "1.7rem", lineHeight: 1.1 }}>{m.title}</h3>
                <p className="mt-1 text-sm" style={{ color: t.soft }}>{m.date} · {m.place}</p>
                <p className="mt-2 max-w-md text-sm">{m.note}</p>
              </div>
            </div>
          ) : (
            <div
              key={i}
              className="w-full max-w-[260px]"
              style={{
                backgroundColor: "#fff",
                padding: tilt ? "10px 10px 22px" : themeKey === "heritage" ? "8px" : "12px",
                borderRadius: rounded ? "18px" : themeKey === "heritage" ? "2px" : "4px",
                transform: tilt ? (i % 2 ? "rotate(1.5deg)" : "rotate(-2deg)") : "none",
                boxShadow: themeKey === "minimal" ? "0 8px 24px rgba(0,0,0,0.06)" : "0 6px 18px rgba(0,0,0,0.12)",
                border: themeKey === "heritage" ? `1px solid ${t.accent}55` : "none",
              }}
            >
              <div style={{ position: "relative", padding: themeKey === "heritage" ? "6px" : 0, border: themeKey === "heritage" ? `1px solid ${t.accent}33` : "none" }}>
                <img
                  src={PHOTOS[i]}
                  alt=""
                  className="aspect-[4/3] w-full object-cover"
                  style={{ filter: sepia ? "sepia(0.55) contrast(0.95)" : "none", borderRadius: rounded ? "12px" : "0" }}
                />
                <span
                  className="absolute left-2 top-2 px-2 py-0.5 text-[10px] text-white"
                  style={{ backgroundColor: t.accent, borderRadius: "999px" }}
                >
                  {m.cat}
                </span>
              </div>
              <div className="px-1 pt-3">
                {tilt && <p style={{ fontFamily: t.script, fontSize: "1.5rem", color: t.accent, lineHeight: 1 }}>{m.date}</p>}
                {!tilt && <p className="text-xs" style={{ color: t.soft }}>{m.date}</p>}
                <h3 style={{ fontFamily: t.serif, fontSize: "1.25rem", lineHeight: 1.15, marginTop: 4 }}>{m.title}</h3>
                <p className="mt-1 flex items-center gap-1 text-xs" style={{ color: t.soft }}>
                  <Users size={12} weight="duotone" /> {m.who}
                </p>
                <p className="mt-0.5 flex items-center gap-1 text-xs" style={{ color: t.soft }}>
                  <MapPin size={12} weight="duotone" /> {m.place}
                </p>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}
